import { useEffect, useMemo, memo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useActivities } from "../hooks/useActivities";
import { useGarminIntegration, useStravaIntegration } from "../hooks/useIntegrations";
import type { ActivityListItem, ActivitySource } from "@runlens/shared";

function formatDistance(meters: number): string {
  const km = meters / 1000;
  return `${km.toFixed(1)} km`;
}

function formatDuration(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

function formatPace(secondsPerKm: number | undefined): string {
  if (!secondsPerKm) return "-";
  const mins = Math.floor(secondsPerKm / 60);
  const secs = Math.floor(secondsPerKm % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

function formatDateShort(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function getDayOfWeek(dateString: string): string {
  const date = new Date(dateString);
  const days = ["Su", "M", "T", "W", "Th", "F", "Sa"];
  return days[date.getDay()];
}

function getWeekKey(dateString: string): string {
  const date = new Date(dateString);
  // Get Monday of the week (Monday = start of week)
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(date.setDate(diff));
  return monday.toISOString().split("T")[0];
}

function formatWeekLabel(weekKey: string): string {
  const monday = new Date(weekKey);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);

  const now = new Date();
  const thisWeekKey = getWeekKey(now.toISOString());
  const lastWeekMonday = new Date(thisWeekKey);
  lastWeekMonday.setDate(lastWeekMonday.getDate() - 7);
  const lastWeekKey = lastWeekMonday.toISOString().split("T")[0];

  if (weekKey === thisWeekKey) {
    return "This week";
  }
  if (weekKey === lastWeekKey) {
    return "Last week";
  }

  const formatOpts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  const start = monday.toLocaleDateString("en-US", formatOpts);
  const end = sunday.toLocaleDateString("en-US", formatOpts);
  return `${start} - ${end}`;
}

interface WeekGroup {
  weekKey: string;
  label: string;
  activities: ActivityListItem[];
}

function groupActivitiesByWeek(activities: ActivityListItem[]): WeekGroup[] {
  const groups = new Map<string, ActivityListItem[]>();

  for (const activity of activities) {
    const weekKey = getWeekKey(activity.startedAt);
    const existing = groups.get(weekKey) || [];
    existing.push(activity);
    groups.set(weekKey, existing);
  }

  // Sort weeks descending (most recent first)
  const sortedKeys = Array.from(groups.keys()).sort((a, b) => b.localeCompare(a));

  return sortedKeys.map((weekKey) => ({
    weekKey,
    label: formatWeekLabel(weekKey),
    // Sort activities within week: youngest first
    activities: groups.get(weekKey)!.sort((a, b) =>
      new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()
    ),
  }));
}

// Metric icons
function DistanceIcon() {
  return (
    <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function DurationIcon() {
  return (
    <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function PaceIcon() {
  return (
    <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  );
}

function HeartIcon() {
  return (
    <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
  );
}

// Source badge icons
function UploadIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
    </svg>
  );
}

function CloudSyncIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
    </svg>
  );
}

function StravaIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169" />
    </svg>
  );
}

function SourceBadge({ source }: { source: ActivitySource }) {
  const config = {
    garmin: {
      icon: <CloudSyncIcon />,
      label: "Garmin",
      className: "text-blue-600 bg-blue-50",
    },
    fit_upload: {
      icon: <UploadIcon />,
      label: "FIT",
      className: "text-purple-600 bg-purple-50",
    },
    strava: {
      icon: <StravaIcon />,
      label: "Strava",
      className: "text-orange-600 bg-orange-50",
    },
  };

  const { icon, label, className } = config[source] || config.fit_upload;

  return (
    <div
      className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${className}`}
      title={label}
    >
      {icon}
    </div>
  );
}

// Memoized to prevent re-renders when parent state changes but activity data is the same
const ActivityCard = memo(function ActivityCard({ activity }: { activity: ActivityListItem }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md hover:border-gray-300 transition-all cursor-pointer">
      {/* Header: day badge + name + source + date */}
      <div className="flex items-center gap-3 mb-3">
        <div className="w-8 h-8 bg-brand-100 text-brand-600 rounded-lg flex items-center justify-center flex-shrink-0 font-semibold text-sm">
          {getDayOfWeek(activity.startedAt)}
        </div>
        <span className="font-medium text-gray-900 flex-1 truncate">
          {activity.name}
        </span>
        <SourceBadge source={activity.source} />
        <span className="text-xs text-gray-500 flex-shrink-0">
          {formatDateShort(activity.startedAt)}
        </span>
      </div>

      {/* Metrics row */}
      <div className="flex items-center gap-3 text-sm text-gray-600 flex-wrap">
        <span className="flex items-center gap-1">
          <DistanceIcon />
          <span className="font-medium">{formatDistance(activity.distance)}</span>
        </span>
        <span className="flex items-center gap-1">
          <DurationIcon />
          <span>{formatDuration(activity.duration)}</span>
        </span>
        {activity.averagePace && (
          <span className="flex items-center gap-1">
            <PaceIcon />
            <span>{formatPace(activity.averagePace)}/km</span>
          </span>
        )}
        {activity.averageHR && (
          <span className="flex items-center gap-1">
            <HeartIcon />
            <span>{activity.averageHR}</span>
          </span>
        )}
      </div>
    </div>
  );
});

function EmptyState({ onUpload, hasConnectedIntegration }: {
  onUpload: () => void;
  hasConnectedIntegration: boolean;
}) {
  return (
    <div className="bg-white rounded-lg shadow p-8 text-center">
      <div className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-gray-100 mb-4">
        <svg
          className="w-8 h-8 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 10V3L4 14h7v7l9-11h-7z"
          />
        </svg>
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">No activities yet</h3>
      <p className="text-gray-500 mb-4">
        Upload a FIT file or connect an integration to sync your activities.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button
          type="button"
          onClick={onUpload}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
        >
          <UploadIcon />
          Upload .FIT
        </button>
        {!hasConnectedIntegration && (
          <Link
            to="/app/settings/integrations"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Connect Integration
          </Link>
        )}
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-col gap-3">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
            <div className="h-4 flex-1 bg-gray-200 rounded"></div>
            <div className="w-12 h-3 bg-gray-100 rounded"></div>
          </div>
          <div className="flex items-center gap-3">
            <div className="h-3 w-14 bg-gray-200 rounded"></div>
            <div className="h-3 w-12 bg-gray-200 rounded"></div>
            <div className="h-3 w-16 bg-gray-200 rounded"></div>
          </div>
        </div>
      ))}
    </div>
  );
}

function SyncProgress({ syncing, syncResult, syncError, syncNeedsReconnect, syncRetryAfter, onDismiss }: {
  syncing: boolean;
  syncResult: { synced: number; skipped: number; errors: number } | null;
  syncError: string | null;
  syncNeedsReconnect?: boolean;
  syncRetryAfter?: number | null;
  onDismiss: () => void;
}) {
  if (!syncing && !syncResult && !syncError) return null;

  const isReconnectError = syncError && syncNeedsReconnect;
  const isRateLimited = syncError && !!syncRetryAfter;

  return (
    <div className={`mb-4 p-4 rounded-lg ${
      isReconnectError || isRateLimited ? "bg-amber-50 border border-amber-200" :
      syncError ? "bg-red-50 border border-red-200" :
      syncing ? "bg-blue-50 border border-blue-200" :
      "bg-green-50 border border-green-200"
    }`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {syncing && (
            <>
              <svg className="w-5 h-5 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span className="text-blue-700">Syncing activities...</span>
            </>
          )}
          {isReconnectError && (
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span className="text-amber-800">
                Session expired.{" "}
                <Link to="/app/settings/integrations" className="underline hover:no-underline font-medium">
                  Reconnect your account
                </Link>
              </span>
            </div>
          )}
          {isRateLimited && !isReconnectError && (
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-amber-800">{syncError}</span>
            </div>
          )}
          {syncError && !isReconnectError && !isRateLimited && (
            <span className="text-red-700">Sync failed: {syncError}</span>
          )}
          {syncResult && !syncing && (
            <span className="text-green-700">
              Synced {syncResult.synced} activities
              {syncResult.skipped > 0 && ` (${syncResult.skipped} already existed)`}
              {syncResult.errors > 0 && ` (${syncResult.errors} errors)`}
            </span>
          )}
        </div>
        {!syncing && (
          <button
            type="button"
            onClick={onDismiss}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

function UploadFeedback({ uploading, uploadError, uploadDuplicate, onDismiss }: {
  uploading: boolean;
  uploadError: string | null;
  uploadDuplicate: ActivityListItem | null;
  onDismiss: () => void;
}) {
  if (!uploading && !uploadError && !uploadDuplicate) return null;

  return (
    <div className={`mb-4 p-4 rounded-lg ${
      uploadError ? "bg-red-50 border border-red-200" :
      uploading ? "bg-blue-50 border border-blue-200" :
      "bg-yellow-50 border border-yellow-200"
    }`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {uploading && (
            <>
              <svg className="w-5 h-5 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span className="text-blue-700">Uploading activity...</span>
            </>
          )}
          {uploadError && (
            <span className="text-red-700">Upload failed: {uploadError}</span>
          )}
          {uploadDuplicate && (
            <span className="text-yellow-700">
              This activity already exists:{" "}
              <Link to={`/app/activities/${uploadDuplicate.id}`} className="underline hover:no-underline">
                {uploadDuplicate.name}
              </Link>
            </span>
          )}
        </div>
        {!uploading && (
          <button
            type="button"
            onClick={onDismiss}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

export function ActivitiesPage() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showFirstSyncPrompt, setShowFirstSyncPrompt] = useState(false);

  const {
    activities,
    loading,
    error,
    uploading,
    uploadError,
    uploadDuplicate,
    syncing,
    syncResult,
    syncError,
    syncNeedsReconnect,
    syncRetryAfter,
    fetchActivities,
    uploadFile,
    syncFromProvider,
    clearUploadState,
    clearSyncState,
  } = useActivities({ autoFetch: true });

  const {
    isConnected: isGarminConnected,
    statusLoading: garminStatusLoading,
  } = useGarminIntegration();

  const {
    isConnected: isStravaConnected,
    statusLoading: stravaStatusLoading,
  } = useStravaIntegration();

  const hasConnectedIntegration = isGarminConnected || isStravaConnected;

  // Show first sync prompt if connected but no activities
  useEffect(() => {
    if (hasConnectedIntegration && !loading && activities.length === 0 && !syncing) {
      setShowFirstSyncPrompt(true);
    }
  }, [hasConnectedIntegration, loading, activities.length, syncing]);

  // Memoize week grouping to prevent recalculation on every render
  const weekGroups = useMemo(() => groupActivitiesByWeek(activities), [activities]);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input so same file can be selected again
    e.target.value = "";

    // Upload the file directly
    await uploadFile(file);
  };

  const handleGarminSync = async () => {
    setShowFirstSyncPrompt(false);
    await syncFromProvider("garmin");
  };

  const handleStravaSync = async () => {
    await syncFromProvider("strava");
  };

  const isLoading = loading || garminStatusLoading || stravaStatusLoading;

  return (
    <div>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".fit"
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Activities</h1>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleUploadClick}
            disabled={uploading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors"
          >
            <UploadIcon />
            <span className="hidden sm:inline">Upload</span>
          </button>
          {isGarminConnected && (
            <button
              type="button"
              onClick={handleGarminSync}
              disabled={syncing || loading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              <CloudSyncIcon />
              <span className="hidden sm:inline">{syncing ? "Syncing..." : "Sync Garmin"}</span>
            </button>
          )}
          {isStravaConnected && (
            <button
              type="button"
              onClick={handleStravaSync}
              disabled={syncing || loading || !!syncRetryAfter}
              className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors"
            >
              <StravaIcon />
              <span className="hidden sm:inline">{syncing ? "Syncing..." : "Sync Strava"}</span>
            </button>
          )}
        </div>
      </div>

      {/* Sync progress */}
      <SyncProgress
        syncing={syncing}
        syncResult={syncResult}
        syncError={syncError}
        syncNeedsReconnect={syncNeedsReconnect}
        syncRetryAfter={syncRetryAfter}
        onDismiss={clearSyncState}
      />

      {/* Upload feedback */}
      <UploadFeedback
        uploading={uploading}
        uploadError={uploadError}
        uploadDuplicate={uploadDuplicate}
        onDismiss={clearUploadState}
      />

      {/* First sync prompt */}
      {showFirstSyncPrompt && hasConnectedIntegration && !syncing && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-800 font-medium">Sync your activities</p>
              <p className="text-blue-600 text-sm">Import your running history to get started.</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowFirstSyncPrompt(false)}
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                Later
              </button>
              {isGarminConnected && (
                <button
                  type="button"
                  onClick={handleGarminSync}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Sync Garmin
                </button>
              )}
              {isStravaConnected && (
                <button
                  type="button"
                  onClick={handleStravaSync}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                >
                  Sync Strava
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {isLoading && activities.length === 0 ? (
        <LoadingState />
      ) : error ? (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            <p className="font-medium">Failed to load activities</p>
            <p className="text-sm mt-1">{error}</p>
            <button
              type="button"
              onClick={() => fetchActivities()}
              className="mt-3 text-sm underline hover:no-underline"
            >
              Try again
            </button>
          </div>
        </div>
      ) : activities.length === 0 ? (
        <EmptyState
          onUpload={handleUploadClick}
          hasConnectedIntegration={hasConnectedIntegration}
        />
      ) : (
        <div className="flex flex-col gap-3">
          {weekGroups.map((group) => (
            <div key={group.weekKey}>
              <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2 px-1">
                {group.label}
              </div>
              {/* Activities in this week */}
              <div className="flex flex-col gap-3">
                {group.activities.map((activity) => (
                  <Link to={`/app/activities/${activity.id}`} key={activity.id}>
                    <ActivityCard activity={activity} />
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}
